import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DecimalPipe, NgClass, NgForOf, NgIf, PercentPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatChipsModule} from '@angular/material/chips';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatRadioModule} from '@angular/material/radio';
import {MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {combineLatest, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {
  CriterionDef,
  EvaluationAudioClip,
  EvaluationComment,
  EvaluationDecision,
  EvaluationGame,
  EvaluationSeason,
  EvidenceEvent,
  Reward,
  STATE_LABELS,
} from '../../model/evaluation/evaluation';
import {hasRole, isEvaluationAdmin, User} from '../../model/user';
import {AuthService} from '../../services/auth.service';
import {EvaluationsService} from '../../services/evaluations.service';
import {declarationPays} from '../declaration';
import {
  blockedReason,
  computeReward,
  effectiveValue,
  maximumCut,
  pendingCriteria,
  readyToRelease,
} from '../reward';
import {EvidenceAudioComponent} from '../evidence-audio.component';
import {EvidenceImageComponent} from '../evidence-image.component';
import {RecordingPlayerComponent} from '../recording-player.component';

interface CriterionRow {
  def: CriterionDef;
  value: number | null;
  measuredValue: number | null;
  review: boolean;
  note: string;
  at: number | null;
  decision: EvaluationDecision | null;
  evidence: EvidenceEvent[];
}

/**
 * Which evidence belongs under which criterion. `index` counts the stills of
 * the same badge family in broadcast order, which is how the two lineup
 * graphics are told apart.
 */
const EVIDENCE_FOR: { [criterionId: string]: (event: EvidenceEvent, index: number) => boolean } = {
  // One still per detected viewpoint, taken while the scoreboard graphic was
  // up — so the row shows the cameras. It used to show the corner-marker
  // stills, which are frames of sponsor messages: the one thing in the
  // broadcast that proves nothing about a camera.
  cameras: event => event.kind.startsWith('camera:'),
  scoreboard: event => event.kind.startsWith('scoreboard'),
  continuity: event => event.kind.startsWith('outage'),
  opener: event => event.kind === 'marker' && event.slot === 'opener',
  sponsor01: event => event.slot === 'SV 01b',
  sponsor03: event => event.slot === 'SV 03b',
  sponsor05: event => event.slot === 'SV 05b',
  sponsor07: event => event.slot === 'SV 07b',
  sponsor08: event => event.slot === 'SV 08b',
  // The lineups are not told apart by kind, and no longer by order either —
  // see `lineupStills`. These two are only the fallback for a game published
  // before the tool started saying which presentation is which.
  lineupsT1: (event, index) => index < 2,
  lineupsM1: (event, index) => index >= 2 && index < 4,
  // A replay is proved either by the pair of branded transitions around it or
  // by the league's REPLAY overlay; the stills come from inside the replay.
  replay: event => event.kind.startsWith('replay:') || event.kind === 'badge:replay',
};

/** The two graphics a lineup presentation is made of, in broadcast order. */
const LINEUP_KINDS = ['badge:lineup', 'badge:defense'];

/**
 * One broadcast, end to end: what was measured, what it pays, and the buttons
 * that move it through the workflow.
 *
 * Both roles use this screen, because arguing about a game means looking at the
 * same thing. What differs is what each may do with it.
 */
@Component({
  selector: 'app-evaluation-game-detail',
  templateUrl: './game-detail.component.html',
  styleUrls: ['./game-detail.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatTableModule,
    MatTooltipModule,
    EvidenceAudioComponent,
    EvidenceImageComponent,
    RecordingPlayerComponent,
    DecimalPipe,
    PercentPipe,
    NgClass,
    NgForOf,
    NgIf,
  ]
})
export class EvaluationGameDetailComponent implements OnInit, OnDestroy {

  readonly stateLabels = STATE_LABELS;

  user: User;
  game: EvaluationGame;
  season: EvaluationSeason;
  decisions: { [criterion: string]: EvaluationDecision } = {};
  comments: EvaluationComment[] = [];
  rows: CriterionRow[] = [];
  reward: Reward;
  pending: string[] = [];
  blocked = '';

  /** Open editor state, keyed by criterion — an inline form per row. */
  noteDraft: { [criterion: string]: string } = {};
  /** Cut in whole percent for the criteria an admin dials; see `applyCut`. */
  cutDraft: { [criterion: string]: number } = {};
  editing: string | null = null;
  copiedPath = false;

  @ViewChild('player') player: RecordingPlayerComponent;
  commentDraft = '';
  returnCriterion: string | null = null;
  returnDraft = '';
  returning = false;
  /** The withdraw button asks once; it takes the game off a producer's screen. */
  withdrawing = false;
  /** The broadcast is not in the season on screen; see the subscription. */
  missing = false;
  /** Disputing asks for a reason; the producer is the one who reads it. */
  disputing = false;
  disputeDraft = '';
  busy = false;

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private evaluations: EvaluationsService,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(this.auth.user$.subscribe(user => this.user = user));
    this.subscriptions.add(
      this.route.params.pipe(
        switchMap(params => combineLatest([
          this.evaluations.game(params['videoId']),
          this.evaluations.season(),
          this.evaluations.decisions(params['videoId']),
          this.evaluations.comments(params['videoId']),
        ]))
      ).subscribe(([game, season, decisions, comments]) => {
        // A document that came back empty while the season is loaded means
        // the broadcast is not in *this* season — a link kept from last one,
        // most likely. Without this the screen waits for something that is
        // never coming and says "Loading…" for ever.
        this.missing = !game && !!season;
        this.game = game;
        this.season = season;
        this.comments = comments;
        this.decisions = {};
        (decisions ?? []).forEach(decision => this.decisions[decision.criterion] = decision);
        this.rebuild();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * The lineup stills, and whether the tool said which presentation each one
   * belongs to.
   *
   * It used to be pure order — first LINEUP/DEFENSE pair is T1, second is M1 —
   * which is the very guess the tool dropped once it could read the batting
   * team off the graphic's colour. On a broadcast with a single presentation
   * the two disagreed: the verdict row said M1 while the stills sat under T1.
   * Order stays as the fallback, for games published before the tool knew.
   */
  private lineupStills(): {stills: EvidenceEvent[]; byCriterion: boolean} {
    const stills = (this.game.events ?? [])
      .filter(event => LINEUP_KINDS.includes(event.kind))
      .sort((a, b) => a.start - b.start);
    return {stills, byCriterion: stills.some(event => !!event.criterion)};
  }

  /** The stills that prove one criterion. */
  private evidenceFor(criterion: string,
                      lineups: {stills: EvidenceEvent[]; byCriterion: boolean}): EvidenceEvent[] {
    const isLineups = criterion.startsWith('lineups');
    if (isLineups && lineups.byCriterion) {
      return lineups.stills.filter(event => event.criterion === criterion);
    }
    const matcher = EVIDENCE_FOR[criterion];
    if (!matcher) {
      return [];
    }
    const pool = isLineups ? lineups.stills : (this.game.events ?? []);
    return pool.filter((event, index) => matcher(event, index));
  }

  private rebuild(): void {
    if (!this.game || !this.season) {
      return;
    }
    const lineups = this.lineupStills();
    this.rows = this.season.criteria.map(def => {
      const measured = this.game.measurements?.[def.id];
      return {
        def,
        value: effectiveValue(def.id, this.game, this.decisions),
        measuredValue: measured?.value ?? null,
        review: measured?.review ?? false,
        note: measured?.note ?? '',
        at: measured?.at ?? null,
        decision: this.decisions[def.id] ?? null,
        evidence: this.evidenceFor(def.id, lineups),
      };
    });
    this.reward = computeReward(this.game, this.season, this.decisions);
    this.pending = pendingCriteria(this.game, this.season, this.decisions);
    this.blocked = blockedReason(this.reward, this.season);
  }

  // --- roles ---------------------------------------------------------------

  get isAdmin(): boolean {
    return isEvaluationAdmin(this.user);
  }

  /**
   * An admin who also holds the producer role still works as an admin here:
   * approving one's own evaluation would be approving one's own pay.
   */
  get isProducer(): boolean {
    return !this.isAdmin && hasRole(this.user, 'producer');
  }

  /** An admin rules while the game is still internal, or after it came back. */
  get canDecide(): boolean {
    return this.isAdmin && (this.game?.state === 'toDecide' || this.game?.state === 'returned');
  }

  /**
   * The scoreboard graphic was up for less than half the recording, so the
   * stretch the game-time criteria were measured over is short. Worth saying
   * out loud: below the tool's own floor it stops using the mask at all and
   * measures the whole broadcast instead.
   */
  /** The sound sample taken from the lineup presentation, if the tool made one. */
  get lineupAudio(): EvaluationAudioClip | null {
    return (this.game?.audioClips ?? []).find(clip => clip.kind === 'lineups') ?? null;
  }

  /** Jump the player to a criterion's first occurrence. */
  seek(seconds: number | null): void {
    this.player?.seekTo(seconds);
  }

  /** What the sample is, in words — where it starts and why it might not be at the lineups. */
  get audioCaption(): string {
    const clip = this.lineupAudio;
    if (!clip) {
      return '';
    }
    return clip.movedPastSilence
      ? `Sound from ${this.time(clip.start)} — the lineup graphic itself was silent, `
        + 'so this is the first stretch with any sound'
      : `Sound from the lineup presentation, ${this.time(clip.start)} `
        + '— a commentator is talking here if the broadcast has one';
  }

  /**
   * Chrome refuses to follow a file:// link from a page served over http, and
   * says nothing when it blocks one. Copying the path is the honest version of
   * the same thing: paste it into a player.
   */
  copyLocalFile(event: Event): void {
    if (!this.game?.localFile) {
      return;
    }
    event.preventDefault();
    navigator.clipboard?.writeText(this.game.localFile);
    this.copiedPath = true;
    setTimeout(() => this.copiedPath = false, 2000);
  }

  get shortPlaytime(): boolean {
    const share = this.game?.facts?.playShare;
    return share !== undefined && share !== null && share < 0.5;
  }

  get canRelease(): boolean {
    return this.canDecide && readyToRelease(this.game, this.season, this.decisions);
  }

  /**
   * An admin may pull a game back for as long as the producer has not approved
   * it. Approved means an agreed amount, and taking that back silently is the
   * argument the whole workflow exists to avoid.
   */
  get canWithdraw(): boolean {
    return this.isAdmin
      && (this.game?.state === 'toApprove' || this.game?.state === 'returned');
  }

  /** The producer acts only on a game that has been handed to them. */
  get canAct(): boolean {
    return this.isProducer && (this.game?.state === 'toApprove' || this.game?.state === 'returned');
  }

  get declaration(): number | null {
    const value = this.game?.declarations?.operatedCamera;
    return value === undefined ? null : value;
  }

  get declarationConfirmed(): boolean {
    return !!this.game?.declarations?.confirmedAt;
  }

  get declarationDisputed(): boolean {
    return !!this.game?.declarations?.disputedAt;
  }

  /** A claim worth 825 or 1650, and so the only kind an admin confirms. */
  get declarationPays(): boolean {
    return declarationPays(this.game);
  }

  /** Declared, and an admin has neither agreed nor objected. */
  get declarationOpen(): boolean {
    return !this.declarationConfirmed && !this.declarationDisputed;
  }

  // --- display -------------------------------------------------------------

  verdict(row: CriterionRow): string {
    if (row.value === null) {
      return row.review ? 'to decide' : 'not measured';
    }
    if (row.def.id === 'extraVideos') {
      return `${row.value}`;
    }
    if (row.value === 1) {
      return 'met';
    }
    // A criterion that is dialled rather than answered says what it costs.
    // "not met" on a row worth up to 40 % of the fee, next to a 90 % ruling
    // that costs four, would be two very different things reading the same.
    const cut = this.cutOf(row);
    if (cut !== null) {
      return `−${Math.round(cut * 100)}% of the production fee`;
    }
    if (row.value === 0) {
      return 'not met';
    }
    return `${Math.round(row.value * 100)}%`;
  }

  verdictClass(row: CriterionRow): string {
    if (row.value === null) {
      return row.review ? 'warn' : 'muted';
    }
    if (row.def.id === 'extraVideos') {
      return 'muted';
    }
    return row.value > 0 ? 'ok' : 'bad';
  }

  time(seconds: number | null): string {
    if (seconds === null || seconds === undefined) {
      return '';
    }
    const whole = Math.floor(seconds);
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const rest = whole % 60;
    const pad = (value: number) => value.toString().padStart(2, '0');
    return hours ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
  }

  criterionLabel(id: string | null): string {
    if (!id) {
      return 'the broadcast as a whole';
    }
    return this.season?.criteria.find(criterion => criterion.id === id)?.label ?? id;
  }

  // --- admin actions -------------------------------------------------------

  startEditing(row: CriterionRow): void {
    this.editing = row.def.id;
    this.noteDraft[row.def.id] = row.decision?.note ?? '';
    const cut = this.cutOf(row);
    this.cutDraft[row.def.id] = cut === null ? 0 : Math.round(cut * 100);
  }

  // --- criteria an admin dials rather than answers -------------------------

  /**
   * The largest cut this criterion can take off the production fee, as whole
   * percent — or null for the criteria that only have two answers. It is what
   * bounds the input, so the admin cannot hand out a cut the contract does not
   * have a rate for.
   */
  maximumCutPercent(row: CriterionRow): number | null {
    const share = maximumCut(row.def.id, this.season);
    return share === null ? null : Math.round(share * 100);
  }

  /** What this row currently costs the production fee, as a share of it. */
  cutOf(row: CriterionRow): number | null {
    const share = maximumCut(row.def.id, this.season);
    if (share === null || row.value === null) {
      return null;
    }
    return (1 - Math.max(0, Math.min(1, row.value))) * share;
  }

  /**
   * Store a dialled cut as what it really is — how much of the criterion was
   * met. The value, not the cut, is what the reward is computed from and what
   * a producer sees, so a later change to the rate re-prices old rulings
   * instead of leaving them frozen at a percentage nobody can trace.
   */
  async applyCut(row: CriterionRow): Promise<void> {
    const maximum = this.maximumCutPercent(row);
    if (maximum === null) {
      return;
    }
    const cut = Math.max(0, Math.min(maximum, this.cutDraft[row.def.id] ?? 0));
    await this.decide(row, maximum === 0 ? 1 : 1 - cut / maximum);
  }

  async decide(row: CriterionRow, value: number | null): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.decide(this.game.videoId, row.def.id, value,
        this.noteDraft[row.def.id] ?? '', row.measuredValue);
      this.editing = null;
    } finally {
      this.busy = false;
    }
  }

  async confirmDeclaration(): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.confirmDeclaration(this.game.videoId);
    } finally {
      this.busy = false;
    }
  }

  /**
   * Disputing takes a reason because the producer reads it: the note is
   * posted to the comment thread, which is where every other objection on
   * this game already lives.
   */
  async disputeDeclaration(): Promise<void> {
    const note = this.disputeDraft.trim();
    if (!note) {
      return;
    }
    this.busy = true;
    try {
      await this.evaluations.disputeDeclaration(this.game.videoId, note);
      this.disputing = false;
      this.disputeDraft = '';
    } finally {
      this.busy = false;
    }
  }

  async release(): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.release([this.game]);
    } finally {
      this.busy = false;
    }
  }

  /** Back to the admin's desk. Confirmed first — it disappears from a producer's list. */
  async withdraw(): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.withdraw(this.game.videoId);
      this.withdrawing = false;
    } finally {
      this.busy = false;
    }
  }

  // --- producer actions ----------------------------------------------------

  async declareCamera(value: number): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.declareOperatedCamera(this.game.videoId, value);
    } finally {
      this.busy = false;
    }
  }

  async approve(): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.approve(this.game.videoId, this.reward);
    } finally {
      this.busy = false;
    }
  }

  async returnGame(): Promise<void> {
    if (!this.returnDraft.trim()) {
      return;
    }
    this.busy = true;
    try {
      await this.evaluations.returnGame(this.game.videoId, this.returnCriterion, this.returnDraft.trim());
      this.returning = false;
      this.returnDraft = '';
      this.returnCriterion = null;
    } finally {
      this.busy = false;
    }
  }

  // --- comments ------------------------------------------------------------

  async addComment(): Promise<void> {
    if (!this.commentDraft.trim()) {
      return;
    }
    this.busy = true;
    try {
      await this.evaluations.addComment(this.game.videoId, null, this.commentDraft.trim());
      this.commentDraft = '';
    } finally {
      this.busy = false;
    }
  }

  async resolveComment(comment: EvaluationComment): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.resolveComment(this.game.videoId, comment.id);
    } finally {
      this.busy = false;
    }
  }

  back(): void {
    this.router.navigate([this.isAdmin ? '/evaluation/season' : '/evaluation/my-games']);
  }
}
